import { useEffect, useId, useRef, useState } from "react";
import {
  AccessibilityInfo,
  findNodeHandle,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildPauseForCell,
  numericFieldErrorText,
  SET_PAUSE_PHASES,
  type BlockMember,
  type SetPausePhase,
} from "@heraion/shared";

import { stateAria } from "../a11y";
import { Icon } from "../Icon/Icon";
import { pauseLabel, pausePhaseMeaning, pausePhaseName } from "../pauseWords";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";

type Row = BlockMember["setRows"][number];
type Choice = "none" | SetPausePhase;

const CHOICES: readonly Choice[] = ["none", ...SET_PAUSE_PHASES];

// HI-8: the pause inside the rep of one set. Closed, one summary row as tall
// as a set row («Pause · Isometric: 3 s hold»); tapped, it opens the phase
// chips and the seconds, typed like the rest seconds (ComposerSetRow). The
// table keeps one set open at a time. «None» clears both. Every keystroke is
// already in the draft, so closing or opening another set loses nothing.
export function ComposerSetPause({
  row,
  setLabel,
  expanded,
  onToggle,
  onUpdateRow,
  styles,
  theme,
}: {
  row: Row;
  /** The set's number as the table prints it. */
  setLabel: string;
  /** This set's chips and seconds are open (one set at a time, the table's). */
  expanded: boolean;
  onToggle: () => void;
  onUpdateRow: (patch: Partial<Row>) => void;
  styles: ComposerStyles;
  theme: UiTheme;
}) {
  const { t } = useTranslation();
  const local = makeLocalStyles(theme);
  const errorId = useId();
  const groupRef = useRef<View>(null);
  // The error speaks once the author has been in the field, or has closed the
  // set: a live region born full is not announced, so it stays mounted and
  // empty until then.
  const [touched, setTouched] = useState(false);
  const wasExpanded = useRef(expanded);
  useEffect(() => {
    if (wasExpanded.current && !expanded) setTouched(true);
    wasExpanded.current = expanded;
    // The reader lands on the chips it just opened. Native only, as in
    // TimeZoneChoice: on web they follow the summary in tab order.
    if (!expanded || Platform.OS === "web") return;
    const tag = groupRef.current === null ? null : findNodeHandle(groupRef.current);
    if (tag !== null) AccessibilityInfo.setAccessibilityFocus(tag);
  }, [expanded]);
  const none = t("program.pause.none", { defaultValue: "None" });
  const title = t("program.pause.title", { defaultValue: "Pause" });
  const phase = row.pausePhase;
  const sec = (row.pauseSec ?? "").trim();
  // Said beside the field, never by the border alone; empty asks the question
  // a countdown with no length asks.
  const error =
    phase === undefined
      ? null
      : sec === ""
        ? t("program.workSecondsRequired", { defaultValue: "How long?" })
        : numericFieldErrorText("pauseSeconds", sec, t);
  const shown = touched ? error : null;
  // The same words the logger reads; a phase still without seconds, its name.
  const built = buildPauseForCell(phase, row.pauseSec).pause;
  const summary = built
    ? t("mobile.logger.pauseRow", {
        phase: pausePhaseName(t, built.phase),
        label: pauseLabel(t, built),
        defaultValue: "{{phase}}: {{label}}",
      })
    : phase
      ? pausePhaseName(t, phase)
      : none;
  const groupName = t("program.pause.groupSet", {
    n: setLabel,
    defaultValue: "Pause inside the reps of set {{n}}",
  });
  return (
    <View style={styles.rangeBlock}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`${groupName}: ${summary}`}
        accessibilityState={{ expanded }}
        {...stateAria({ expanded })}
        onPress={onToggle}
        style={local.summary}
      >
        <Text style={styles.chipsLabel}>{title}</Text>
        <Text style={local.summaryText} numberOfLines={2}>
          {summary}
        </Text>
        <Icon name={expanded ? "chevron-up" : "chevron-down"} size={theme.typography.sizes.base} color={theme.palette.ink3} />
      </Pressable>
      {expanded ? (
        <>
          <ChipChoiceRow<Choice>
            theme={theme}
            choices={CHOICES}
            selected={phase ?? "none"}
            onSelect={(c) =>
              onUpdateRow(c === "none" ? { pausePhase: undefined, pauseSec: undefined } : { pausePhase: c })
            }
            label={(c) => (c === "none" ? none : pausePhaseName(t, c))}
            chipA11yLabel={(c) => (c === "none" ? none : pausePhaseName(t, c))}
            // The ear gets the meaning after the name: the term arrives explained.
            chipA11yHint={(c) => (c === "none" ? undefined : pausePhaseMeaning(t, c))}
            groupA11yLabel={groupName}
            groupRef={groupRef}
            wrapLabels
          />
          {phase !== undefined ? (
            <View style={styles.chipsLine}>
              <Text style={styles.chipsLabel}>{t("program.pause.seconds", { defaultValue: "Pause (s)" })}</Text>
              <View style={styles.chipsRow}>
                <TextInput
                  style={[styles.cellInput, styles.secondsInput, shown !== null && styles.cellInputInvalid]}
                  value={row.pauseSec ?? ""}
                  onChangeText={(v) => {
                    setTouched(true);
                    onUpdateRow({ pauseSec: v });
                  }}
                  onBlur={() => setTouched(true)}
                  // «Done» on the keyboard confirms: the set closes.
                  returnKeyType="done"
                  onSubmitEditing={onToggle}
                  keyboardType="number-pad"
                  accessibilityLabel={t("program.pause.secondsSet", {
                    n: setLabel,
                    defaultValue: "Pause seconds, set {{n}}",
                  })}
                  // The error is tied to the field: by id on the web, as the hint
                  // natively (RN has no described-by). aria-invalid as TextField.
                  accessibilityHint={shown ?? undefined}
                  {...(Platform.OS === "web"
                    ? { "aria-describedby": errorId, ...(shown !== null ? { "aria-invalid": true } : {}) }
                    : {})}
                />
              </View>
            </View>
          ) : null}
        </>
      ) : null}
      {/* Mounted open or closed: a set closed without its seconds still says so. */}
      <Text nativeID={errorId} accessibilityLiveRegion="polite" style={styles.validationText}>
        {shown ?? ""}
      </Text>
    </View>
  );
}

function makeLocalStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    // As tall as a set row: the 44 floor on its own box, no slop needed.
    summary: { flexDirection: "row", alignItems: "center", gap: s.s3, minHeight: 44 },
    summaryText: { flex: 1, color: p.ink, fontSize: ty.sizes.sm, fontVariant: ["tabular-nums"] },
  });
}
